import { Info, KeyRound } from 'lucide-react';
import type { CapabilityState, StorageCapability, StorageResourceRef } from '../../../../../types/azureStorage';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { useStorageAccountInfoStore } from '../../../../database-objects/store/storageAccountInfoStore';
import { trackStorageEvent } from '../../services/storageAnalytics';
import type { StorageToolbarContext } from '../../toolbar/toolbarExtensions';
import { useStorageDialogStore } from '../dialogs/storageDialogStore';
import { openStoragePropertiesPanel } from '../propertiesPanelBridge';
import { sasTargetForRow, sasTargetProblem } from '../sasTargets';
import { StorageToolbarMenuItem } from '../../toolbar/StorageToolbarMenuItem';

type Capabilities = Partial<Record<StorageCapability, CapabilityState>>;

/** The toolbar target: the one selected item, else the location the tab shows. */
export function s3ToolbarTarget(context: StorageToolbarContext): StorageResourceRef {
  return context.selection.length === 1 ? context.selection[0] : context.resource;
}

/** S8 (Task 8.7): on a confirmed-HNS account a selected folder is a Data Lake directory, so Get SAS… signs a directory SAS. */
function s3SasToolbarTarget(context: StorageToolbarContext): StorageResourceRef {
  const target = s3ToolbarTarget(context);
  if (target.kind !== 'blobPrefix') return target;
  const hns = context.capabilities.hns === 'yes' ? 'yes' : 'unknown';
  return sasTargetForRow({ item: { kind: 'folder', container: target.container, prefix: target.prefix } }, { hns }) ?? target;
}

const tabIdOf = (context: StorageToolbarContext): string | undefined =>
  typeof context.capabilities.tabId === 'string' ? context.capabilities.tabId : undefined;

const refusal = (state: CapabilityState | undefined): string | undefined =>
  state?.state === 'unsupported' || state?.state === 'unavailable' ? state.reason : undefined;

export function sasCapabilityRefusal(capabilities: Capabilities): string | undefined {
  const service = capabilities['service-sas'];
  const delegation = capabilities['user-delegation-sas'];
  if (service?.state === 'supported' || delegation?.state === 'supported') return undefined;
  return refusal(service ?? delegation);
}

/**
 * Properties' action and reason (C1 R8): never a native disabled button with a title only. Review C1-02: a refused
 * listing does not refuse Properties — Azure authorizes Get Container Properties apart from List Blobs, so the panel
 * attempts its own read and shows its own refusal if that fails too.
 */
function usePropertiesAction(context: StorageToolbarContext): { reason?: string; run: () => void } {
  const tabId = tabIdOf(context);
  const reason = !tabId ? 'Properties open from a storage tab.' : undefined;
  return { reason, run: () => {
    if (reason) return;
    trackStorageEvent('toolbar:storage_properties', { open: true });
    openStoragePropertiesPanel(tabId, s3ToolbarTarget(context));
  } };
}

export function S3PropertiesToolbarButton({ context }: { context: StorageToolbarContext }) {
  const { reason, run } = usePropertiesAction(context);
  return (
    <ReasonButton variant="outline" size="xs" className="gap-1" title="Properties" reason={reason} data-testid="storage-toolbar-properties" onClick={run}>
      <Info className="h-3.5 w-3.5" /> Properties
    </ReasonButton>
  );
}

/** Its More-menu form when the action row runs out of width (C1 R5 / N2). */
export function S3PropertiesMenuItem({ context }: { context: StorageToolbarContext }) {
  const { reason, run } = usePropertiesAction(context);
  return <StorageToolbarMenuItem icon={Info} label="Properties" reason={reason} onSelect={run} testId="storage-toolbar-properties" />;
}

export function S3SasToolbarButton({ context }: { context: StorageToolbarContext }) {
  const entry = useStorageAccountInfoStore((state) => state.byConnection[context.connectionId]);
  const capabilities: Capabilities = entry?.status === 'ready' ? entry.info.capabilities ?? {} : {};
  const target = s3SasToolbarTarget(context);
  const reason = sasTargetProblem(target) ?? sasCapabilityRefusal(capabilities);
  // A More-menu entry (UI/UX fix round A, A4): a real menu item that closes before the dialog opens.
  return <StorageToolbarMenuItem icon={KeyRound} label="Get SAS…" reason={reason} testId="storage-toolbar-sas" onSelect={() => {
    useStorageDialogStore.getState().openDialog({ kind: 'sas', connectionId: context.connectionId, resource: target, source: 'toolbar' });
  }} />;
}
